/**
 * Landing Page v3 - implemented from "Vishwa Landing Page v3.dc.html"
 * (claude.ai/design project "Vishwalab website redesign").
 *
 * v3 changes vs. v2: wordmark logo in the nav, "LIVE ON MAINNET" hero badge,
 * two-row proof bar (proof points + trusted-by logos), light "primitive"
 * section with the authorize→move→attest funnel diagram, REQUEST/RESPONSE
 * developer panels with a BUILT WITH bar, light "Live today" cards with
 * status pills, headshot testimonial cards ("Backed by"), a light
 * security/assurance section with legal disclaimer, and a legal footer.
 *
 * Self-contained page: renders its own nav and footer per the design,
 * so it is mounted outside the shared <Layout /> in the router.
 */
import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import styles from './index.module.scss'
import { APP_URL, MCP_CLI_URL, DOCS_URL, CONTACT_URL } from '@/consts/Links.ts'
import Faq from './Faq'
import Nav from '@/components/LandingChrome/Nav'
import Footer from '@/components/LandingChrome/Footer'
import wordmark from '@/assets/image/header/logo.png'

// Trusted-by logos. Existing assets are white marks (built for dark sections);
// the proof bar is light, so they're filtered to ink in CSS. Ondo has no asset
// in the repo yet - rendered as a text wordmark until the real logo is added.
import logoPharos from '@/assets/image/ecosystem/icon_Pharos.png'
import logoFidelity from '@/assets/image/home/brand2/IconFideLitys.png'
import logoBitGo from '@/assets/image/ecosystem/icon_Bitgo.png'
import logoAnchorage from '@/assets/image/ecosystem/icon_AnchorageDigital.png'
import logoSolana from '@/assets/image/ecosystem/icon_Solana.png'
import logo0G from '@/assets/image/ecosystem/Icon0G.png'
import logoKite from '@/assets/image/ecosystem/icon_kiteAI.png'

const Logo = ({ width = 22, height = 20, stroke = '#16140F', dot = '#15915E', style }) => (
  <svg width={width} height={height} viewBox="0 0 22 20" fill="none" aria-hidden="true" style={style}>
    <path d="M11 2 L20 18 L2 18 Z" stroke={stroke} strokeWidth="1.6" strokeLinejoin="round" />
    <circle cx="11" cy="2" r="2.1" fill={dot} />
  </svg>
)

// Scroll-reveal: fade+rise elements marked [data-reveal] as they enter the viewport.
const useReveal = (rootRef) => {
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const els = Array.from(root.querySelectorAll('[data-reveal]'))
    els.forEach((el) => {
      el.style.opacity = '0'
      el.style.transform = 'translateY(26px)'
      const d = el.getAttribute('data-reveal-delay') || '0'
      el.style.transition = `opacity .8s cubic-bezier(.2,.7,.2,1) ${d}ms, transform .8s cubic-bezier(.2,.7,.2,1) ${d}ms`
    })
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.style.opacity = '1'
            e.target.style.transform = 'none'
            io.unobserve(e.target)
          }
        })
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    )
    els.forEach((el) => io.observe(el))
    return () => io.disconnect()
  }, [rootRef])
}

const GateDiagram = () => (
  <svg
    viewBox="0 0 1080 452"
    className={styles.gateSvg}
    fontFamily="Archivo, sans-serif"
    aria-label="Every agent request and banking function passes through the Vishwa control and enforcement gate"
  >
    <defs>
      <marker id="vsh-in" markerWidth="7" markerHeight="7" refX="5.5" refY="3" orient="auto">
        <path d="M0 0 L6 3 L0 6 Z" fill="#5E7E70" />
      </marker>
      <radialGradient id="vsh-glow" cx="50%" cy="72%" r="60%">
        <stop offset="0%" stopColor="#5FE3A1" stopOpacity="0.22" />
        <stop offset="100%" stopColor="#5FE3A1" stopOpacity="0" />
      </radialGradient>
    </defs>

    <text x="380" y="92" textAnchor="end" fontFamily="IBM Plex Mono, monospace" fontSize="12" letterSpacing="1.6" fill="#6E737D">WHAT IT CHECKS</text>
    <text x="714" y="92" textAnchor="start" fontFamily="IBM Plex Mono, monospace" fontSize="12" letterSpacing="1.6" fill="#6E737D">WHAT IT UNLOCKS</text>
    <text x="540" y="40" textAnchor="middle" fontFamily="IBM Plex Mono, monospace" fontSize="12" letterSpacing="1.6" fill="#6E737D">EVERY AGENT REQUEST</text>
    <line x1="540" y1="54" x2="540" y2="104" stroke="#5E7E70" strokeWidth="1.4" markerEnd="url(#vsh-in)" />

    <path d="M392 120 L461 224" stroke="#414751" strokeWidth="1.2" fill="none" markerEnd="url(#vsh-in)" />
    <path d="M392 188 L461 234" stroke="#414751" strokeWidth="1.2" fill="none" markerEnd="url(#vsh-in)" />
    <path d="M392 256 L461 246" stroke="#414751" strokeWidth="1.2" fill="none" markerEnd="url(#vsh-in)" />
    <path d="M392 324 L461 256" stroke="#414751" strokeWidth="1.2" fill="none" markerEnd="url(#vsh-in)" />

    <path d="M619 230 L703 150" stroke="#414751" strokeWidth="1.2" fill="none" markerEnd="url(#vsh-in)" />
    <path d="M619 236 L703 232" stroke="#414751" strokeWidth="1.2" fill="none" markerEnd="url(#vsh-in)" />
    <path d="M619 242 L703 312" stroke="#414751" strokeWidth="1.2" fill="none" markerEnd="url(#vsh-in)" />

    <path d="M466 362 L466 200 A74 74 0 0 1 614 200 L614 362 Z" fill="url(#vsh-glow)" stroke="#2E6F54" strokeWidth="1.6" />
    <path d="M484 362 L484 204 A56 56 0 0 1 596 204 L596 362" fill="none" stroke="#1F4A38" strokeWidth="1" />
    <circle cx="586" cy="236" r="4" fill="#5FE3A1" />
    <text x="540" y="228" textAnchor="middle" fontSize="25" fontWeight="700" letterSpacing="-0.3" fill="#5FE3A1">Control</text>
    <text x="540" y="254" textAnchor="middle" fontSize="15" fontWeight="500" fill="#3E8E69">+</text>
    <text x="540" y="288" textAnchor="middle" fontSize="25" fontWeight="700" letterSpacing="-0.3" fill="#5FE3A1">Enforcement</text>

    <text x="380" y="126" textAnchor="end" fontSize="21" fontWeight="700" fill="#E8EAED">Identity</text>
    <text x="380" y="147" textAnchor="end" fontSize="14" fill="#6E737D">who is allowed to act</text>
    <text x="380" y="194" textAnchor="end" fontSize="21" fontWeight="700" fill="#E8EAED">Authorization</text>
    <text x="380" y="215" textAnchor="end" fontSize="14" fill="#6E737D">what they can do</text>
    <text x="380" y="262" textAnchor="end" fontSize="21" fontWeight="700" fill="#E8EAED">Solvency</text>
    <text x="380" y="283" textAnchor="end" fontSize="14" fill="#6E737D">the funds are actually there</text>
    <text x="380" y="330" textAnchor="end" fontSize="21" fontWeight="700" fill="#E8EAED">Policy</text>
    <text x="380" y="351" textAnchor="end" fontSize="14" fill="#6E737D">limits, sanctions, AML</text>

    <text x="714" y="148" textAnchor="start" fontSize="21" fontWeight="700" fill="#E8EAED">Payments</text>
    <text x="714" y="169" textAnchor="start" fontSize="14" fill="#6E737D">move across rails</text>
    <text x="714" y="230" textAnchor="start" fontSize="21" fontWeight="700" fill="#E8EAED">Treasury</text>
    <text x="714" y="251" textAnchor="start" fontSize="14" fill="#6E737D">hold &amp; schedule reserves</text>
    <text x="714" y="312" textAnchor="start" fontSize="21" fontWeight="700" fill="#E8EAED">Credit</text>
    <text x="714" y="333" textAnchor="start" fontSize="14" fill="#6E737D">deploy against limits</text>

    <text x="540" y="414" textAnchor="middle" fontFamily="IBM Plex Mono, monospace" fontSize="14" letterSpacing="0.6" fill="#8A9088">Nothing reaches execution without passing through.</text>
  </svg>
)

const FunnelDiagram = () => (
  <svg
    width="440"
    height="673"
    viewBox="0 0 340 520"
    fill="none"
    className={styles.funnelSvg}
    aria-label="Every agent request funnels through the authorize checkpoint, then move, then attest, then settles"
  >
    <defs>
      <marker id="vp-a" markerWidth="7" markerHeight="7" refX="5.5" refY="3" orient="auto">
        <path d="M0 0 L6 3 L0 6 Z" fill="#A8987E" />
      </marker>
      <marker id="vp-g" markerWidth="7" markerHeight="7" refX="5.5" refY="3" orient="auto">
        <path d="M0 0 L6 3 L0 6 Z" fill="#15915E" />
      </marker>
    </defs>
    <text x="170" y="14" textAnchor="middle" fontFamily="IBM Plex Mono,monospace" fontSize="12" fontWeight="600" letterSpacing="1.4" fill="#8A8472">EVERY REQUEST</text>
    <path d="M126 30 L162 110" stroke="#C9BFA9" strokeWidth="1.2" markerEnd="url(#vp-g)" />
    <path d="M170 30 L170 108" stroke="#C9BFA9" strokeWidth="1.2" markerEnd="url(#vp-g)" />
    <path d="M214 30 L178 110" stroke="#C9BFA9" strokeWidth="1.2" markerEnd="url(#vp-g)" />
    <path d="M106 245 L106 162 A64 64 0 0 1 234 162 L234 245 L204 245 L204 168 A34 34 0 0 0 136 168 L136 245 Z" fill="rgba(21,145,94,.08)" stroke="#15915E" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M106 245 L80 261" stroke="#15915E" strokeWidth="3" strokeLinecap="round" />
    <path d="M234 245 L260 261" stroke="#15915E" strokeWidth="3" strokeLinecap="round" />
    <circle cx="210" cy="150" r="3.5" fill="#15915E" />
    <text x="170" y="204" textAnchor="middle" fontFamily="IBM Plex Mono,monospace" fontSize="16" fontWeight="700" letterSpacing="0.5" fill="#15915E">AUTHORIZE</text>
    <text x="170" y="278" textAnchor="middle" fontFamily="Hanken Grotesk,sans-serif" fontSize="12" fill="#8A8472">Policy · Exposure · Compliance</text>
    <line x1="170" y1="290" x2="170" y2="314" stroke="#A8987E" strokeWidth="1.3" markerEnd="url(#vp-a)" />
    <rect x="106" y="318" width="128" height="54" rx="9" fill="#fff" stroke="#16140F" strokeWidth="1.4" />
    <text x="170" y="342" textAnchor="middle" fontFamily="IBM Plex Mono,monospace" fontSize="13" fontWeight="600" fill="#16140F">MOVE</text>
    <text x="170" y="360" textAnchor="middle" fontFamily="Hanken Grotesk,sans-serif" fontSize="11.5" fill="#8A8472">any rail, one interface</text>
    <line x1="170" y1="376" x2="170" y2="400" stroke="#A8987E" strokeWidth="1.3" markerEnd="url(#vp-a)" />
    <rect x="106" y="404" width="128" height="54" rx="9" fill="#fff" stroke="#16140F" strokeWidth="1.4" />
    <text x="170" y="428" textAnchor="middle" fontFamily="IBM Plex Mono,monospace" fontSize="13" fontWeight="600" fill="#16140F">ATTEST</text>
    <text x="170" y="446" textAnchor="middle" fontFamily="Hanken Grotesk,sans-serif" fontSize="11.5" fill="#8A8472">signed record 0x9f…</text>
    <line x1="170" y1="462" x2="170" y2="486" stroke="#A8987E" strokeWidth="1.3" markerEnd="url(#vp-a)" />
    <circle cx="170" cy="497" r="6.5" fill="#16140F" />
    <text x="170" y="519" textAnchor="middle" fontFamily="IBM Plex Mono,monospace" fontSize="12" fontWeight="600" letterSpacing="1.4" fill="#8A8472">SETTLED</text>
  </svg>
)

// Trusted-by logo wall. `src: null` falls back to a text wordmark (Ondo has no
// logo asset in the repo yet).
const trustedBy = [
  { name: 'Pharos', src: logoPharos },
  { name: 'Fidelity Labs', src: logoFidelity },
  { name: 'BitGo', src: logoBitGo },
  { name: 'Anchorage', src: logoAnchorage },
  { name: 'Ondo', src: null },
  { name: 'Solana', src: logoSolana },
  { name: '0G', src: logo0G },
  { name: 'Kite', src: logoKite },
]

const voices = [
  {
    name: 'Rishin Sharma',
    initials: 'RS',
    role: 'Head of AI Growth, Solana',
    img: '/voices/Rish.png',
    quote: 'It’s a big jump from letting your agent write something for you to actually letting it manage literal dollars.',
    link: 'https://x.com/Vishwa_lab/status/2074327479905583290',
  },
  {
    name: 'Bruno Batavia',
    initials: 'BB',
    role: 'Partner, Valor Capital Group · ex-Banco Central do Brasil',
    img: '/voices/Bruno.png',
    quote: 'The future is not blind autonomy for agents. It is controlled autonomy.',
    link: 'https://x.com/Vishwa_lab/status/2071972478382227787',
  },
  {
    name: 'Canice Wu',
    initials: 'CW',
    role: 'RVP, Salesforce & Oracle',
    img: '/voices/Canice.png',
    quote: 'You need to move at agentic speed, but you also need the controls in place for execution to be accountable and traceable.',
    link: 'https://x.com/Vishwa_lab/status/2071972478382227787',
  },
]

const LandingV2 = () => {
  const rootRef = useRef(null)
  useReveal(rootRef)

  return (
    <div className={styles.page} ref={rootRef}>
      <Nav />

      {/* Hero */}
      <section className={styles.hero} id="top">
        <div className={styles.heroGrid} />
        <div className={styles.heroInner}>
          <div data-reveal className={styles.badge}>
            <span className={styles.badgeDot} />LIVE ON MAINNET
          </div>
          <h1 data-reveal data-reveal-delay="80" className={styles.heroTitle}>
            Humans have banking infrastructure.<br /><span>Agents don&rsquo;t.</span>
          </h1>
          <div data-reveal data-reveal-delay="140" className={styles.heroSub}>
            Vishwa is the banking infrastructure for autonomous capital.
          </div>
          <p data-reveal data-reveal-delay="200" className={styles.heroPara}>
            It handles end-to-end financial operations through execution controls, expanding what AI can safely execute.
          </p>
          <div data-reveal data-reveal-delay="280" className={styles.ctaRow}>
            <a className={styles.ctaPrimary} href={CONTACT_URL} target="_blank" rel="noreferrer">Talk to us →</a>
            <a className={styles.ctaSecondary} href={MCP_CLI_URL} target="_blank" rel="noreferrer">Start with the CLI</a>
          </div>
        </div>

        <div data-reveal data-reveal-delay="320" className={styles.terminal}>
          <div className={styles.termCmd}>$ vishwa move 2.4M USDC → treasury</div>
          <div className={styles.termCheck}>
            &nbsp;&nbsp;↳ treasury policy <span>✓</span>&nbsp;&nbsp;exposure limit <span>✓</span>&nbsp;&nbsp;OFAC <span>✓</span>
          </div>
          <div className={styles.termOk}>&nbsp;&nbsp;✓ authorized in 14ms · proof 0x9f... <span className={styles.termCursor} /></div>
        </div>

        <div data-reveal data-reveal-delay="340" className={styles.metrics}>
          <div className={styles.metric}>
            <div className={styles.metricValue}>$170M</div>
            <div className={styles.metricLabel}>LIVE TODAY</div>
          </div>
          <div className={styles.metric}>
            <div className={styles.metricValue}>{'<15ms'}</div>
            <div className={styles.metricLabel}>AUTHORIZATION LATENCY</div>
          </div>
          <div className={styles.metric}>
            <div className={`${styles.metricValue} ${styles.accent}`}>#1</div>
            <div className={styles.metricLabel}>ON X402SCAN</div>
          </div>
        </div>
      </section>

      {/* Proof bar */}
      <section className={styles.proofBar}>
        <div className={styles.proofInner}>
          <div className={styles.proofRow}>
            <span className={styles.trustLabel}>Trusted by</span>
            <div className={styles.trustLogos}>
              {trustedBy.map((b) => (
                b.src
                  ? <img key={b.name} src={b.src} alt={b.name} title={b.name} />
                  : <span key={b.name}>{b.name}</span>
              ))}
            </div>
          </div>
          <div className={`${styles.proofRow} ${styles.trustRow}`}>
            <span className={styles.proofCredential}>
              Selected for <strong>Anthropic&rsquo;s</strong> Cyber Verification Program
            </span>
          </div>
        </div>
      </section>

      {/* 01 - The problem */}
      <section className={`${styles.sectionDark} ${styles.problem}`} id="problem">
        <div className={styles.problemGlow} />
        <div className={styles.inner}>
          <div data-reveal className={styles.eyebrow}>01 - The problem</div>
          <h2 data-reveal className={styles.problemTitle}>
            Approval isn&rsquo;t control.<br /><span>Execution is.</span>
          </h2>
          <p data-reveal data-reveal-delay="100" className={styles.problemPara}>
            A bank is fundamentally a gate - every move checked before it happens. Agents have
            no gate. They run straight from intent to settlement, with nothing in between.
          </p>
          <div data-reveal data-reveal-delay="160" className={styles.gateWrap}>
            <GateDiagram />
          </div>
        </div>
      </section>

      {/* 02 - The primitive */}
      <section className={styles.primitive} id="primitive">
        <div className={styles.inner}>
          <div data-reveal className={styles.eyebrow}>02 - The primitive</div>
          <h2 data-reveal className={styles.primitiveTitle}>Three steps stand between intent and settlement.</h2>
          <p data-reveal className={styles.primitivePara}>
            Every agent request passes through the same gate: authorized against policy, moved
            across a rail, and attested on-chain.
          </p>
          <div className={styles.primitiveGrid}>
            <div data-reveal className={styles.funnelWrap}>
              <FunnelDiagram />
            </div>
            <div className={styles.checkCards}>
              <div data-reveal className={styles.checkCard}>
                <div className={`${styles.checkCardStep} ${styles.accent}`}>01 / AUTHORIZE</div>
                <h3 className={styles.checkCardTitle}>Banking controls, before execution</h3>
                <p className={styles.checkCardPara}>
                  Limits, allowlists, velocity and counterparty rules enforced at the moment of
                  request - bad moves rejected in under 15ms.
                </p>
              </div>
              <div data-reveal data-reveal-delay="100" className={styles.checkCard}>
                <div className={styles.checkCardStep}>02 / MOVE</div>
                <h3 className={styles.checkCardTitle}>Any rail, one interface</h3>
                <p className={styles.checkCardPara}>
                  Stablecoins, x402, FX and traditional banking behind a single neutral API - across
                  payments, treasury and real-world assets.
                </p>
              </div>
              <div data-reveal data-reveal-delay="200" className={styles.checkCard}>
                <div className={styles.checkCardStep}>03 / ATTEST</div>
                <h3 className={styles.checkCardTitle}>Every decision, attestable</h3>
                <p className={styles.checkCardPara}>
                  Each authorization writes a signed, on-chain record - a continuous audit trail
                  for auditors and regulators.
                </p>
              </div>
            </div>
          </div>

          <div data-reveal data-reveal-delay="240" className={styles.beforeAfter}>
            <div className={styles.beforeAfterGrid}>
              <div className={styles.beforeCell}>
                <div className={`${styles.baLabel} ${styles.bad}`}>CONTROL AFTER EXECUTION</div>
                <div className={styles.baFlow}>
                  execute → settle → <span className={styles.hlBad}>review</span><br />
                  ↳ irreversible after settlement
                </div>
              </div>
              <div className={styles.afterCell}>
                <div className={`${styles.baLabel} ${styles.good}`}>CONTROL BEFORE EXECUTION</div>
                <div className={`${styles.baFlow} ${styles.baFlowGood}`}>
                  request → <span className={styles.hlGood}>authorize</span> → execute<br />
                  ↳ nothing moves out of policy
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 03 - The developer surface */}
      <section className={`${styles.sectionDark} ${styles.developers}`} id="developers">
        <div className={styles.devGridBg} />
        <div className={styles.inner}>
          <div data-reveal className={styles.eyebrow}>03 - The developer surface</div>
          <h2 data-reveal className={styles.devTitle}>One API in front of every move.</h2>
          <p data-reveal className={styles.devPara}>
            Declare policy once. Every agent request is checked against it - and every
            authorization returns a proof.
          </p>

          <div data-reveal data-reveal-delay="100" className={styles.apiPanels}>
            <div className={styles.apiPanel}>
              <div className={styles.apiPanelHead}>
                <span className={styles.apiPanelKind}>REQUEST</span>
                <span className={styles.apiPanelPath}>POST /v1/transfers</span>
              </div>
              <div className={styles.codeLight}>{'{'}</div>
              <div className={styles.codeDim}>&nbsp;&nbsp;"agent": <span>"agt_7f2c"</span>,</div>
              <div className={styles.codeDim}>&nbsp;&nbsp;"amount": <span>"2400000"</span>,</div>
              <div className={styles.codeDim}>&nbsp;&nbsp;"asset": <span className={styles.codeGreen}>"USDC"</span>,</div>
              <div className={styles.codeDim}>&nbsp;&nbsp;"destination": <span>"treasury_main"</span></div>
              <div className={styles.codeLight}>{'}'}</div>
            </div>
            <div className={styles.apiPanel}>
              <div className={styles.apiPanelHead}>
                <span className={styles.apiPanelKind}>RESPONSE</span>
                <span className={styles.apiPanelPath}>200 · 14ms</span>
              </div>
              <div className={styles.codeLight}>{'{'}</div>
              <div className={styles.codeDim}>&nbsp;&nbsp;"status": <span className={styles.codeGreen}>"authorized"</span>,</div>
              <div className={styles.codeDim}>
                &nbsp;&nbsp;"checks": {'{'} "policy": <span className={styles.codeGreen}>"pass"</span>, "limits": <span className={styles.codeGreen}>"pass"</span>, "velocity": <span className={styles.codeGreen}>"pass"</span> {'}'},
              </div>
              <div className={styles.codeDim}>&nbsp;&nbsp;"proof": <span>"0x9f3a…c41e"</span></div>
              <div className={styles.codeLight}>{'}'}</div>
            </div>
          </div>

          <div data-reveal data-reveal-delay="160" className={styles.builtWith}>
            <div className={styles.builtWithLeft}>
              <span className={styles.builtWithLabel}>BUILT WITH</span>
              <div className={styles.builtWithChips}>
                <a className={styles.builtWithChip} href={MCP_CLI_URL} target="_blank" rel="noreferrer">Vishwa CLI</a>
                <a className={styles.builtWithChip} href={APP_URL} target="_blank" rel="noreferrer">Vishwa Vault</a>
                <Link className={styles.builtWithChip} to="/erc">ERC-8150</Link>
              </div>
            </div>
            <div className={styles.builtWithCtas}>
              <a className={styles.devPrimary} href={MCP_CLI_URL} target="_blank" rel="noreferrer">Start with the CLI →</a>
              <a className={styles.devSecondary} href={APP_URL} target="_blank" rel="noreferrer">Open Vault</a>
            </div>
          </div>
        </div>
      </section>

      {/* 04 - Backed by */}
      <section className={`${styles.sectionDark} ${styles.voices}`} id="voices">
        <div className={styles.inner}>
          <div data-reveal className={styles.eyebrow}>04 - What they say</div>
          <h2 data-reveal className={styles.voicesTitle}>How partners talk about us.</h2>
          <div className={styles.voicesGrid}>
            {voices.map((v, i) => (
              <div
                data-reveal
                data-reveal-delay={(i % 3) * 80}
                className={`${styles.voiceCard} ${v.pending ? styles.pending : ''}`}
                key={v.name}
              >
                <div className={styles.headshot} aria-label={`Headshot - ${v.name}`}>
                  {v.img ? <img src={v.img} alt={v.name} /> : v.initials}
                </div>
                <div>
                  <div className={styles.voiceName}>{v.name}</div>
                  <div className={styles.voiceRole}>{v.role}</div>
                </div>
                <p className={`${styles.voiceQuote} ${v.pending ? styles.pendingQuote : ''}`}>&ldquo;{v.quote}&rdquo;</p>
                {v.link && (
                  <a className={styles.voiceLink} href={v.link} target="_blank" rel="noreferrer">
                    Watch the conversation →
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 05 - Where Vishwa sits */}
      <section className={`${styles.sectionDark} ${styles.layer}`} id="how">
        <div className={styles.layerGridBg} />
        <div className={styles.inner}>
          <div data-reveal className={styles.eyebrow}>05 - Where Vishwa sits</div>
          <h2 data-reveal className={styles.layerTitle}>For agents, the gate becomes a new infrastructure layer.</h2>
          <div className={styles.layerCols}>
            <div data-reveal className={styles.humanCol}>
              <div className={styles.colLabel}>HUMAN CAPITAL · FOR REFERENCE</div>
              <div className={styles.stack}>
                <div className={styles.stackBox}>Human</div>
                <div className={styles.stackArrow}>↓ instructs</div>
                <div className={styles.stackBox}>Bank<div className={styles.stackBoxSub}>controls the capital</div></div>
                <div className={styles.stackArrow}>↓ controls</div>
                <div className={styles.stackBox}>Capital</div>
              </div>
            </div>
            <div data-reveal data-reveal-delay="120">
              <div className={`${styles.colLabel} ${styles.accent}`}>AUTONOMOUS CAPITAL</div>
              <div className={`${styles.stack} ${styles.agentStack}`}>
                <div className={styles.stackBox}>Agent</div>
                <div className={styles.stackArrow}>↓ instructs</div>
                <div className={styles.stackBox}>
                  Agent-native applications
                  <div className={styles.stackBoxSub}>that can provide financial services</div>
                </div>
                <div className={`${styles.stackArrow} ${styles.stackArrowUp}`}>↑ empowers</div>
                <div className={styles.vishwaBox}>
                  <div className={styles.vishwaBoxName}>Vishwa</div>
                  <div className={styles.vishwaBoxSub}>foundational banking infrastructure</div>
                </div>
                <div className={styles.stackArrow}>↓ controls</div>
                <div className={styles.stackBox}>
                  Capital
                  <div className={styles.capitalChips}>
                    <span className={styles.capitalChip}>Payments</span>
                    <span className={styles.capitalChip}>Treasury</span>
                    <span className={styles.capitalChip}>Credit</span>
                    <span className={styles.capitalChip}>Settlement</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 06 - Security & assurance */}
      <section className={styles.security} id="security">
        <div className={styles.inner}>
          <div data-reveal className={styles.securityHead}>
            <div className={styles.eyebrow}>06 - Security &amp; assurance</div>
            <h2 className={styles.securityTitle}>Built to be checked.</h2>
          </div>
          <div className={styles.securityGrid}>
            <div data-reveal className={styles.securityCard}>
              <div className={styles.securityLabel}>NON-CUSTODIAL BY DESIGN</div>
              <p className={styles.securityPara}>
                Vishwa never holds customer funds or private keys. Capital moves directly between
                you and your counterparties - the control layer sits in front, never in the middle.
              </p>
              <a className={styles.securityCta} href={DOCS_URL} target="_blank" rel="noreferrer">Read the docs →</a>
            </div>
            <div data-reveal data-reveal-delay="80" className={styles.securityCard}>
              <div className={styles.securityLabel}>INDEPENDENTLY AUDITED</div>
              <p className={styles.securityPara}>
                Audit reports are published in full, and Vishwa is selected for
                Anthropic&rsquo;s Cyber Verification Program.
              </p>
              <a className={styles.securityCta} href={DOCS_URL} target="_blank" rel="noreferrer">View audit report →</a>
            </div>
            <div data-reveal data-reveal-delay="160" className={styles.securityCard}>
              <div className={styles.securityLabel}>AN OPEN STANDARD</div>
              <p className={styles.securityPara}>
                Authorization proofs follow <strong>ERC-8150</strong> - signed, on-chain, and
                verifiable by anyone, not just by us.
              </p>
              <Link className={styles.securityCta} to="/erc">Read the ERC-8150 draft →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* 07 - Go deeper */}
      <section className={styles.resources} id="resources">
        <div className={styles.inner}>
          <div data-reveal className={styles.resourcesHead}>
            <div>
              <div className={styles.eyebrow}>07 - Go deeper</div>
              <h2 className={styles.resourcesTitle}>Why this layer exists.</h2>
            </div>
            <Link className={styles.resourcesAll} to="/blog">All resources →</Link>
          </div>
          <div className={styles.resourceCards}>
            <Link data-reveal className={styles.resourceCard} to="/podcast">
              <div className={styles.resourceKind}>PODCAST</div>
              <div className={styles.resourceIcon}>
                <div className={styles.eqBars}>
                  <span style={{ height: 7 }} /><span style={{ height: 14 }} /><span style={{ height: 10 }} /><span style={{ height: 5 }} />
                </div>
              </div>
              <h3 className={styles.resourceTitle}>The Autonomous Capital Podcast</h3>
              <p className={styles.resourcePara}>Conversations on agents, money and the rails between them.</p>
            </Link>
            <Link data-reveal data-reveal-delay="100" className={styles.resourceCard} to="/blogDetail?slug=execution-verification">
              <div className={styles.resourceKind}>BLOG</div>
              <div className={styles.resourceMeta}>LATEST</div>
              <h3 className={styles.resourceTitle}>Why control belongs before execution</h3>
              <p className={styles.resourcePara}>The architecture argument for pre-execution enforcement.</p>
            </Link>
            <Link data-reveal data-reveal-delay="200" className={styles.resourceCard} to="/blogDetail?slug=agent-native-banking">
              <div className={styles.resourceKind}>CASE STUDY</div>
              <div className={styles.resourceMeta}>FEATURED</div>
              <h3 className={styles.resourceTitle}>How a treasury desk went fully autonomous</h3>
              <p className={styles.resourcePara}>From human approvals to policy-enforced agents.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* 08 — FAQ (crawlable Q&A + FAQPage JSON-LD + agent-facing CTA) */}
      <Faq />

      {/* Final CTA */}
      <section className={styles.finalCta}>
        <div className={styles.finalGridBg} />
        <div className={styles.finalInner}>
          <div data-reveal className={styles.finalMark}>
            <img src={wordmark} alt="Vishwa" />
          </div>
          <h2 data-reveal data-reveal-delay="80" className={styles.finalTitle}>
            The capital is already autonomous. The controls shouldn&rsquo;t lag behind.
          </h2>
          <p data-reveal data-reveal-delay="160" className={styles.finalPara}>
            Talk to us - and put enforcement in front of every agent-initiated move.
          </p>
          <div data-reveal data-reveal-delay="220" className={styles.ctaRow}>
            <a className={styles.finalCtaPrimary} href={CONTACT_URL} target="_blank" rel="noreferrer">Talk to us →</a>
            <a className={styles.finalCtaSecondary} href={MCP_CLI_URL} target="_blank" rel="noreferrer">Start with the CLI</a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  )
}

export default LandingV2
